import { useEffect, useMemo, useRef, useState } from 'react';
import type { RenditionSize } from '@shared/utils/photoRendition';
import { addRendition } from '@shared/utils/photoRendition';
import { LOW_RES_RENDITION, HIGH_RES_RENDITION } from './renditions';
import { addSuppressedError } from '@shared/utils/logger';

// If the high-res image loads within this window, skip the crossfade transition.
// Matches the crossfade delay (150ms) — if the image is ready before the
// transition would start playing, there's no visual benefit to animating.
// This catches both memory cache (sync) and disk cache hits (304 revalidation
// adds a round-trip but is still perceptibly instant on production CDNs).
const IMMEDIATE_LOAD_GRACE_MS = 150;

type ProgressiveImageState = Readonly<{
  lowResUrl: string;
  highResUrl: string;
  isHighResLoaded: boolean;
  /**
   * True when the high-res image was available immediately or within the grace
   * window, so the UI can skip the crossfade transition.
   *
   * This is intentionally a UX signal ("was the load perceptibly instant?"),
   * not a literal "came from disk cache" signal.
   */
  isHighResFromCache: boolean;
}>;

type UseProgressiveImageArgs = Readonly<{
  photoUrl: string;
  lowResRendition?: RenditionSize;
  highResRendition?: RenditionSize;
  /** Use the raw photoUrl for both layers (no rendition query). Honors the
   *  `guestSkipPagePhotoRendition` kill switch for environments that must not request renditioned
   *  page-photo URLs. Blur-up still works (blurred raw → sharp raw), just without renditions. */
  skipRendition?: boolean;

  /** Whether the high-res preload sets crossOrigin='anonymous'. Default true (legacy behavior).
   *  Pass false when the display path is non-CORS (CSS background-image / <img>) so the preload
   *  warms the same cache entry the display fetches — otherwise the low-res fades before the
   *  high-res paints (empty flash). See CoverPhoto. */
  preloadCrossOrigin?: boolean;
}>;

/**
 * Manages progressive image loading: computes low-res and high-res URLs,
 * preloads the high-res image in the background, and reports when it's ready.
 *
 * On SSR, URLs are computed synchronously (available in initial render).
 * The useEffect for preloading only fires on the client after hydration.
 *
 * Cache detection uses a two-tier approach:
 * 1. Synchronous: `img.complete && naturalWidth > 0` right after setting src
 *    catches memory cache hits (image decoded and available immediately).
 * 2. Grace timer: A short setTimeout checks if the image loaded within
 *    IMMEDIATE_LOAD_GRACE_MS — catches disk cache hits where the browser
 *    needed a few ms for disk I/O but the load was still perceptibly instant.
 *
 * If the image hasn't loaded by the grace timer, onload eventually fires and
 * marks it loaded WITHOUT the cache flag, so the full crossfade plays.
 */
export const useProgressiveImage = ({
  photoUrl,
  lowResRendition = LOW_RES_RENDITION,
  highResRendition = HIGH_RES_RENDITION,
  skipRendition = false,
  preloadCrossOrigin = true,
}: UseProgressiveImageArgs): ProgressiveImageState => {
  const lowResUrl = useMemo(() => (skipRendition ? photoUrl : addRendition({ url: photoUrl, renditionSize: lowResRendition })), [photoUrl, lowResRendition, skipRendition]);

  const highResUrl = useMemo(() => (skipRendition ? photoUrl : addRendition({ url: photoUrl, renditionSize: highResRendition })), [photoUrl, highResRendition, skipRendition]);

  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  const [isHighResFromCache, setIsHighResFromCache] = useState(false);

  const highResImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!highResUrl) {
      setIsHighResLoaded(false);
      setIsHighResFromCache(false);
      return;
    }

    let isCancelled = false;

    // Reset state for the new URL.
    setIsHighResLoaded(false);
    setIsHighResFromCache(false);

    const img = new Image();
    // Browsers cache CORS and non-CORS responses under separate keys. When the display path is
    // non-CORS (CSS background-image / <img>), a crossOrigin preload warms a different cache entry
    // than the display fetches, so the low-res fades before the high-res paints (empty flash) —
    // those consumers pass preloadCrossOrigin={false}. Default stays true for legacy behavior; the
    // canvas fallback loads its own CORS image regardless.
    if (preloadCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    highResImageRef.current = img;

    const startedAt = performance.now();

    const markLoaded = (fromCacheLike: boolean) => {
      if (isCancelled) return;
      setIsHighResLoaded(true);
      setIsHighResFromCache(fromCacheLike);
    };

    // Use elapsed time to determine if the load was perceptibly instant.
    // This handles all cache scenarios (memory + disk) without a race
    // between onload and the grace timer.
    img.onload = () => {
      const loadedWithinGrace = performance.now() - startedAt <= IMMEDIATE_LOAD_GRACE_MS;
      markLoaded(loadedWithinGrace);
    };

    // On error, leave state unchanged — the blurred low-res layer stays visible.
    // Setting isHighResLoaded would hide the low-res layer and show a blank
    // (since the high-res src is only set when loaded). Blurred low-res is
    // better than nothing. Log to New Relic so we can track CDN failures.
    img.onerror = () => {
      addSuppressedError(new Error('High-res image preload failed'), {
        context: 'useProgressiveImage',
        highResUrl,
      });
    };

    img.src = highResUrl;

    // Synchronous memory cache hit — img.complete is true immediately after
    // setting src. onload won't fire for these, so handle it here.
    if (img.complete && img.naturalWidth > 0) {
      markLoaded(true);
    }

    return () => {
      isCancelled = true;
      img.onload = null;
      img.onerror = null;
      highResImageRef.current = null;
    };
  }, [highResUrl, preloadCrossOrigin]);

  return {
    lowResUrl,
    highResUrl,
    isHighResLoaded,
    isHighResFromCache,
  };
};
